import { KitDetails } from '../../../../services/modules/kit/kitDetails/kitDetails.types';
import { MediaType } from '../../../../types/general.types';
import { ESColorClassification, ESProductMedia, ESProductPrice } from '../../ProductTypes';
import {
  DetailsModalEntity,
  ProductDetailsEntityType,
  SimplePrice,
} from '../types/productDetails.types';
import {
  MappedPropsToContentChildren,
  MountProductDetailsContentPropsToChildrenParams,
} from '../types/productDetailsContent.types';

const isTagClassification = (classification: ESColorClassification) =>
  classification.classificationType.useAsTag;

const mountProductDetailsContentPropsToChildren = ({
  product,
}: MountProductDetailsContentPropsToChildrenParams) => {
  if (!product) {
    return null;
  }

  const availableSizeIds = product.grid?.length
    ? new Set(
        product.grid
          .flatMap((g) => g.items)
          .filter((item) => item.availableForSale)
          .flatMap((item) => item.prices)
          .map((p) => p.productSizeId),
      )
    : null;

  const prices = availableSizeIds
    ? ((product.prices as ESProductPrice[] | null)?.filter((p) =>
        availableSizeIds.has(p.productSizeId),
      ) ?? [])
    : (product.prices ?? []);

  const { colorClassifications } = product;

  return {
    product: product,
    code: product.code,
    name: product.name,
    resume: product.resume,
    prices,
    medias: product.medias,
    description: product.description,
    productId: product.productId,
    productColorId: product.productColorId,
    referenceCode: product.referenceCode,
    productColorName: product.color.name,
    productColorCode: product.color.code,
    tags: [...product.tags, ...colorClassifications.filter(isTagClassification)],
    attributes: colorClassifications
      .filter((classification) => !isTagClassification(classification))
      .map(({ id, name }) => ({ id, name })),
  };
};

const mountKitDetailsPropsToChildren = (kit?: KitDetails) => {
  if (!kit) {
    return null;
  }

  const price: SimplePrice = {
    wholesalePrice: kit.wholesalePrice,
  };
  const media: ESProductMedia = {
    id: 'kit-media',
    type: MediaType.IMAGE,
    url: kit.imageUrl || '',
    position: 0,
    processed: true,
  };

  return {
    product: kit,
    code: kit.code,
    name: kit.name,
    prices: [price],
    medias: [media],
    resume: kit.resume,
    description: kit.description,
  };
};

export const mapEntityTypeMountPropsToChildren = (
  entity?: DetailsModalEntity,
): MappedPropsToContentChildren | null => {
  if (!entity) {
    return null;
  }

  switch (entity.type) {
    case ProductDetailsEntityType.PRODUCT:
      return mountProductDetailsContentPropsToChildren({
        product: entity.data,
      });
    case ProductDetailsEntityType.KIT:
      return mountKitDetailsPropsToChildren(entity.data);
    default:
      return null;
  }
};
